import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    RefreshCw, CheckCircle, XCircle, Clock, AlertTriangle, Edit,
    Search, Send, X, Check, ChevronDown, ChevronUp, Filter,
    Receipt, Package, Split, DollarSign, AlertCircle,
    Building, User, FileText, Tag, Settings, Zap, CheckCircle2,
    Save, Calculator, Percent
} from 'lucide-react';

const CompleteExpenseManager = () => {
    const [expenses, setExpenses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [paymentAccounts, setPaymentAccounts] = useState([]);
    const [expenseAccounts, setExpenseAccounts] = useState([]);
    const [filter, setFilter] = useState('all');
    const [selectedExpenses, setSelectedExpenses] = useState([]);
    const [syncInProgress, setSyncInProgress] = useState(false);
    const [bankValidationInProgress, setBankValidationInProgress] = useState(false);

    // Modal states
    const [editingExpense, setEditingExpense] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editFormData, setEditFormData] = useState({});

    // Itemization states
    const [expandedItemized, setExpandedItemized] = useState(new Set());

    // Apply to all states
    const [showApplyToAllModal, setShowApplyToAllModal] = useState(false);
    const [applyToAllExpense, setApplyToAllExpense] = useState(null);
    const [selectedAccountForAll, setSelectedAccountForAll] = useState('');

    // Line item editing states
    const [editingLineItem, setEditingLineItem] = useState(null);
    const [lineItemEditData, setLineItemEditData] = useState({});

    // Organization state
    const [currentOrganization, setCurrentOrganization] = useState({
        organization_id: '',
        name: ''
    });

    // AI suggestions state
    const [aiSuggestions, setAiSuggestions] = useState({});
    const [aiSuggestionsLoading, setAiSuggestionsLoading] = useState({});

    const [dataLoaded, setDataLoaded] = useState(false);
    const loadingRef = useRef(false);

    const API_BASE_URL = 'https://api.zapbooksai.subraps.com/api';

    // Get authentication headers
    const getAuthHeaders = useMemo(() => {
        const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
        return {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` })
        };
    }, []);

    // ITC Eligibility options
    const itcOptions = [
        { value: 'eligible', label: 'Eligible for ITC', description: 'Business expense - ITC can be claimed' },
        { value: 'ineligible_section17', label: 'Ineligible (Section 17)', description: 'Food/Entertainment - ITC blocked' },
        { value: 'ineligible_others', label: 'Ineligible (Others)', description: 'Personal/Gift expenses' }
    ];

    // Fetch current organization
    const fetchCurrentOrganization = useCallback(async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/token/status`, {
                headers: getAuthHeaders
            });
            const result = await response.json();

            if (result.success && result.data.current_organization) {
                const org = result.data.current_organization;
                setCurrentOrganization(org);
                return org.organization_id;
            } else {
                throw new Error('No current organization found');
            }
        } catch (error) {
            console.error('❌ Error fetching current organization:', error);
            throw error;
        }
    }, [getAuthHeaders]);

    // Safe parse line items - handle corrupt data
    const safeParseLineItems = useCallback((lineItemsData) => {
        if (!lineItemsData) return [];

        // If it's already an array, return it
        if (Array.isArray(lineItemsData)) return lineItemsData;

        // If it's a string, try to parse it
        if (typeof lineItemsData === 'string') {
            // Handle the [object Object] case
            if (lineItemsData === '[object Object]' || lineItemsData.startsWith('[object')) {
                console.warn('🔧 Found corrupt line_items_data, returning empty array');
                return [];
            }

            try {
                const parsed = JSON.parse(lineItemsData);
                return Array.isArray(parsed) ? parsed : [];
            } catch (e) {
                console.error('🔧 Error parsing line_items_data:', e.message);
                return [];
            }
        }

        return [];
    }, []);

    // Calculate validation status for expenses with existing JSON data
    const calculateValidationStatus = useCallback((expense) => {
        const missingFields = [];

        if (!expense.date) missingFields.push({ field: 'date', label: 'Date' });
        if (!expense.items) missingFields.push({ field: 'items', label: 'Item Description' });
        if (!expense.total || expense.total <= 0) missingFields.push({ field: 'total', label: 'Amount' });

        // Payment account validation
        if (!expense.zoho_payment_account_id) {
            if (expense.bank_name && expense.bank_account_number) {
                missingFields.push({
                    field: 'zoho_payment_account_id',
                    label: `Payment Account (${expense.bank_name} ****${expense.bank_account_number.toString().slice(-4)} not mapped)`,
                    requires_bank_validation: true
                });
            } else {
                missingFields.push({ field: 'zoho_payment_account_id', label: 'Payment Account' });
            }
        }

        // For itemized expenses, check if line items have expense accounts
        if (expense.has_multiple_items && expense.line_items_data) {
            const lineItems = safeParseLineItems(expense.line_items_data);
            const missingAccountItems = lineItems.filter(item => !item.expense_account_id);
            if (missingAccountItems.length > 0) {
                missingFields.push({
                    field: 'line_item_accounts',
                    label: `Expense Accounts for ${missingAccountItems.length} line items`
                });
            }
        } else if (!expense.has_multiple_items && !expense.zoho_expense_account_id) {
            missingFields.push({ field: 'zoho_expense_account_id', label: 'Expense Account' });
        }

        const totalFields = 4; // Date, Items, Amount, Accounts
        const validFields = totalFields - missingFields.length;
        const completionPercentage = Math.round((validFields / totalFields) * 100);

        return {
            isValid: missingFields.length === 0,
            missingFields,
            completionPercentage,
            validFields,
            totalFields,
            recordType: expense.has_multiple_items ? 'itemized_expense' : 'expense'
        };
    }, [safeParseLineItems]);

    // Fetch expenses
    const fetchExpenses = useCallback(async () => {
        if (!currentOrganization?.organization_id || loadingRef.current) return;

        loadingRef.current = true;
        try {
            const url = `${API_BASE_URL}/expense-integration/expenses/with-integration-data?company_id=${currentOrganization.organization_id}&limit=100`;
            const response = await fetch(url, { headers: getAuthHeaders });

            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

            const data = await response.json();
            if (data.success) {
                const expensesData = data.data || [];

                // Process expenses with safe parsing
                const processedExpenses = expensesData.map(expense => {
                    // Safely parse line_items_data
                    expense.line_items_data = safeParseLineItems(expense.line_items_data);

                    const validationStatus = calculateValidationStatus(expense);
                    return {
                        ...expense,
                        validationStatus,
                        integration_ready: validationStatus.isValid && !expense.zoho_expense_id
                    };
                });

                setExpenses(processedExpenses);
            } else {
                setExpenses([]);
            }
        } catch (error) {
            console.error('❌ Error fetching expenses:', error);
            setExpenses([]);
        } finally {
            loadingRef.current = false;
        }
    }, [currentOrganization?.organization_id, getAuthHeaders, calculateValidationStatus, safeParseLineItems]);

    // Fetch payment accounts
    const fetchPaymentAccounts = useCallback(async () => {
        if (!currentOrganization?.organization_id) return;

        try {
            const response = await fetch(`${API_BASE_URL}/expense-integration/zoho/payment-accounts?company_id=${currentOrganization.organization_id}`, {
                headers: getAuthHeaders
            });
            const data = await response.json();
            if (data.success) setPaymentAccounts(data.accounts || []);
        } catch (error) {
            console.error('Error fetching payment accounts:', error);
        }
    }, [currentOrganization?.organization_id, getAuthHeaders]);

    // Fetch expense accounts
    const fetchExpenseAccounts = useCallback(async () => {
        if (!currentOrganization?.organization_id) return;

        try {
            const response = await fetch(`${API_BASE_URL}/expense-integration/zoho/expense-accounts?company_id=${currentOrganization.organization_id}`, {
                headers: getAuthHeaders
            });
            const data = await response.json();
            if (data.success) setExpenseAccounts(data.accounts || []);
        } catch (error) {
            console.error('Error fetching expense accounts:', error);
        }
    }, [currentOrganization?.organization_id, getAuthHeaders]);

    // Load all data
    const loadAllData = useCallback(async (force = false) => {
        if (dataLoaded && !force) return;
        if (loadingRef.current && !force) return;

        setLoading(true);
        loadingRef.current = true;

        try {
            if (force) loadingRef.current = false;

            await Promise.all([
                fetchExpenses(),
                fetchPaymentAccounts(),
                fetchExpenseAccounts()
            ]);
            setDataLoaded(true);
        } catch (error) {
            console.error('Error loading data:', error);
        } finally {
            setLoading(false);
            loadingRef.current = false;
        }
    }, [fetchExpenses, fetchPaymentAccounts, fetchExpenseAccounts, dataLoaded]);

    // Initialize component
    useEffect(() => {
        const initializeComponent = async () => {
            try {
                await fetchCurrentOrganization();
            } catch (error) {
                console.error('Failed to initialize component:', error);
            }
        };
        initializeComponent();
    }, [fetchCurrentOrganization]);

    // Load data when organization is available
    useEffect(() => {
        if (currentOrganization?.organization_id && !dataLoaded) {
            loadAllData();
        }
    }, [currentOrganization?.organization_id, dataLoaded, loadAllData]);

    // Add these permission check functions near the top of your component
    const canEdit = useCallback((expense) => {
        return expense.approver_status === 'approved' || !expense.approver_status;
    }, []);

    const canSync = useCallback((expense) => {
        return expense.approver_status === 'approved' || !expense.approver_status;
    }, []);

    const canValidatePayment = useCallback((expense) => {
        return expense.approver_status === 'approved' || !expense.approver_status;
    }, []);

    const canViewItemization = useCallback((expense) => {
        return expense.approver_status === 'approved' || !expense.approver_status;
    }, []);

    const canApplyAccountToAll = useCallback((expense) => {
        return expense.approver_status === 'approved' || !expense.approver_status;
    }, []);

    const getApprovalStatusBadge = useCallback((expense) => {
        const status = expense.approver_status;

        if (!status || status === 'approved') {
            return (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Approved
                    {expense.approved_by && (
                        <span className="ml-1 text-xs">by {expense.approved_by}</span>
                    )}
                </span>
            );
        }

        if (status === 'pending_approval') {
            return (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    <Clock className="w-3 h-3 mr-1" />
                    Pending Approval
                </span>
            );
        }

        if (status === 'rejected') {
            return (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    <XCircle className="w-3 h-3 mr-1" />
                    Rejected
                    {expense.rejection_reason && (
                        <div className="text-xs mt-1" title={expense.rejection_reason}>
                            {expense.rejection_reason.substring(0, 30)}...
                        </div>
                    )}
                </span>
            );
        }

        return null;
    }, []);

    // Force refresh function
    const forceRefresh = useCallback(async () => {
        loadingRef.current = false;
        setDataLoaded(false);
        await loadAllData(true);
    }, [loadAllData]);



    // Calculate tax amounts based on rate and quantity
    const calculateTaxAmounts = useCallback((rate, quantity, cgstRate, sgstRate, igstRate) => {
        const amount = parseFloat(rate || 0) * parseFloat(quantity || 1);
        const cgst = cgstRate ? amount * (parseFloat(cgstRate) / 100) : 0;
        const sgst = sgstRate ? amount * (parseFloat(sgstRate) / 100) : 0;
        const igst = igstRate ? amount * (parseFloat(igstRate) / 100) : 0;
        const totalTax = cgst + sgst + igst;
        const totalWithTax = amount + totalTax;

        return {
            amount,
            cgst_amount: cgst,
            sgst_amount: sgst,
            igst_amount: igst,
            total_tax: totalTax,
            total_with_tax: totalWithTax
        };
    }, []);

    // Handle form data change
    const handleFormDataChange = useCallback((field, value) => {
        setEditFormData(prev => ({ ...prev, [field]: value }));
    }, []);

    // Update line item with enhanced editing capabilities
    const updateLineItemAccount = useCallback(async (expenseId, itemIndex, updates) => {
        try {
            const response = await fetch(`${API_BASE_URL}/expense-integration/expenses/${expenseId}/line-items/${itemIndex}`, {
                method: 'PUT',
                headers: getAuthHeaders,
                body: JSON.stringify(updates)
            });

            const data = await response.json();
            if (data.success) {
                console.log('🔧 Backend update successful:', {
                    expenseId,
                    itemIndex,
                    updates,
                    returnedData: data
                });
                // FIXED: Update the state correctly with the new values
                setExpenses(prev => prev.map(exp => {
                    if (exp.id === expenseId) {
                        // Create new line_items_data array with updated item
                        const newLineItems = [...exp.line_items_data];
                        newLineItems[itemIndex] = {
                            ...newLineItems[itemIndex],
                            ...updates  // This applies the new values
                        };

                        // Create updated expense object
                        const updatedExpense = {
                            ...exp,
                            line_items_data: newLineItems
                        };

                        // Recalculate validation status
                        const validationStatus = calculateValidationStatus(updatedExpense);
                        updatedExpense.validationStatus = validationStatus;
                        updatedExpense.integration_ready = validationStatus.isValid && !updatedExpense.zoho_expense_id;

                        return updatedExpense;
                    }
                    return exp;
                }));

                return true;
            } else {
                alert(`Failed to update line item: ${data.message}`);
                return false;
            }
        } catch (error) {
            console.error('Error updating line item account:', error);
            alert('Error updating line item: ' + error.message);
            return false;
        }
    }, [getAuthHeaders, calculateValidationStatus]);

    // REPLACE your existing getAiExpenseAccountSuggestions function with this:
    const getAiExpenseAccountSuggestions = useCallback(async (description, vendorName, amount, expenseId, itemIndex = null, autoSelect = true) => {
        const key = itemIndex !== null ? `${expenseId}-${itemIndex}` : expenseId;
        setAiSuggestionsLoading(prev => ({ ...prev, [key]: true }));

        try {
            const response = await fetch(`${API_BASE_URL}/expense-integration/ai/expense-account-suggestions`, {
                method: 'POST',
                headers: getAuthHeaders,
                body: JSON.stringify({
                    description,
                    vendor_name: vendorName,
                    amount,
                    company_id: currentOrganization.organization_id,
                    existing_accounts: expenseAccounts
                })
            });

            const data = await response.json();
            if (data.success && data.suggestions.length > 0) {
                const suggestions = data.suggestions;
                const bestSuggestion = suggestions[0];

                console.log('🎯 AI Suggestions received:', suggestions);
                console.log('🎯 Best suggestion:', bestSuggestion);

                if (autoSelect && (bestSuggestion.confidence === 'high' || bestSuggestion.confidence === 'low')) {
                    // For line items
                    if (itemIndex !== null) {
                        console.log(`🔧 Auto-applying to line item ${itemIndex}:`, bestSuggestion);

                        const success = await updateLineItemAccount(expenseId, itemIndex, {
                            expense_account_id: bestSuggestion.account_id,
                            expense_account_name: bestSuggestion.account_name
                        });

                        if (success) {
                            console.log(`✅ Auto-selected for line item ${itemIndex}: ${bestSuggestion.account_name}`);

                            // FORCE STATE UPDATE - This is the key fix
                            setExpenses(prev => prev.map(exp => {
                                if (exp.id === expenseId) {
                                    const newLineItems = [...exp.line_items_data];
                                    if (newLineItems[itemIndex]) {
                                        newLineItems[itemIndex] = {
                                            ...newLineItems[itemIndex],
                                            expense_account_id: bestSuggestion.account_id,
                                            expense_account_name: bestSuggestion.account_name
                                        };
                                    }

                                    const updatedExpense = {
                                        ...exp,
                                        line_items_data: newLineItems
                                    };

                                    // Recalculate validation status
                                    const validationStatus = calculateValidationStatus(updatedExpense);
                                    updatedExpense.validationStatus = validationStatus;
                                    updatedExpense.integration_ready = validationStatus.isValid && !updatedExpense.zoho_expense_id;

                                    return updatedExpense;
                                }
                                return exp;
                            }));
                        }
                    }
                    // For single expense in edit modal
                    else if (editingExpense && editingExpense.id === expenseId) {
                        handleFormDataChange('zoho_expense_account_id', bestSuggestion.account_id);
                        handleFormDataChange('zoho_expense_account_name', bestSuggestion.account_name);
                        console.log(`✅ Auto-selected for expense ${expenseId}: ${bestSuggestion.account_name}`);
                    }

                    // Store remaining suggestions (excluding the auto-selected one)
                    const remainingSuggestions = suggestions.slice(1);
                    setAiSuggestions(prev => ({
                        ...prev,
                        [key]: remainingSuggestions
                    }));

                    return { autoSelected: bestSuggestion, remaining: remainingSuggestions };
                } else {
                    // If not auto-selecting or confidence is not high, show all suggestions
                    setAiSuggestions(prev => ({
                        ...prev,
                        [key]: suggestions
                    }));
                    return { autoSelected: null, remaining: suggestions };
                }
            }
            return { autoSelected: null, remaining: [] };
        } catch (error) {
            console.error('Error getting AI suggestions:', error);
            return { autoSelected: null, remaining: [] };
        } finally {
            setAiSuggestionsLoading(prev => ({ ...prev, [key]: false }));
        }
    }, [getAuthHeaders, currentOrganization.organization_id, expenseAccounts, updateLineItemAccount, editingExpense, handleFormDataChange, calculateValidationStatus]);

    // Fix corrupt line items data
    const fixLineItemsData = useCallback(async (expenseId) => {
        try {
            const response = await fetch(`${API_BASE_URL}/expense-integration/expenses/${expenseId}/fix-line-items`, {
                method: 'POST',
                headers: getAuthHeaders
            });

            const data = await response.json();
            if (data.success) {
                alert(`✅ Fixed ${data.fixed_items_count} line items successfully!`);
                await fetchExpenses();
            } else {
                alert(`❌ Failed to fix line items: ${data.message}`);
            }
        } catch (error) {
            console.error('Error fixing line items:', error);
            alert('Error fixing line items: ' + error.message);
        }
    }, [getAuthHeaders, fetchExpenses]);

    // Bank account validation
    const validateBankAccount = useCallback(async (expense) => {
        setBankValidationInProgress(true);
        try {
            const response = await fetch(`${API_BASE_URL}/expense-integration/expenses/validate-payment-account`, {
                method: 'POST',
                headers: getAuthHeaders,
                body: JSON.stringify({
                    expense_id: expense.id,
                    company_id: currentOrganization.organization_id
                })
            });

            const data = await response.json();

            if (data.success && data.account) {
                await updateExpenseValidation(expense.id, {
                    payment_account_id: data.account.account_id,
                    payment_account_name: data.account.account_name
                });
                alert(`✅ Payment account mapped successfully: ${data.account.account_name}`);
            } else if (data.requires_manual_mapping) {
                alert(`❌ ${data.message}\n\nPlease contact the expense submitter to verify bank details.`);
            } else {
                alert(`⚠️ ${data.message}`);
            }
        } catch (error) {
            console.error('Bank validation error:', error);
            alert('Bank validation failed: ' + error.message);
        } finally {
            setBankValidationInProgress(false);
        }
    }, [getAuthHeaders, currentOrganization.organization_id]);

    // Update expense validation
    const updateExpenseValidation = useCallback(async (expenseId, updates) => {
        try {
            const response = await fetch(`${API_BASE_URL}/expense-integration/expenses/${expenseId}/integration-validation`, {
                method: 'PUT',
                headers: getAuthHeaders,
                body: JSON.stringify(updates)
            });

            const data = await response.json();

            if (data.success) {
                setExpenses(prev => prev.map(e => {
                    if (e.id === expenseId) {
                        const updatedExpense = {
                            ...e,
                            zoho_payment_account_id: updates.payment_account_id || e.zoho_payment_account_id,
                            zoho_payment_account_name: updates.payment_account_name || e.zoho_payment_account_name,
                            zoho_expense_account_id: updates.expense_account_id || e.zoho_expense_account_id,
                            zoho_expense_account_name: updates.expense_account_name || e.zoho_expense_account_name,
                            line_items_data: updates.line_items_data || e.line_items_data,
                            expense_validation_status: updates.validation_status || 'updated'
                        };

                        const validationStatus = calculateValidationStatus(updatedExpense);
                        updatedExpense.validationStatus = validationStatus;
                        updatedExpense.integration_ready = validationStatus.isValid && !updatedExpense.zoho_expense_id;

                        return updatedExpense;
                    }
                    return e;
                }));
            }
        } catch (error) {
            console.error('Error updating expense validation:', error);
        }
    }, [getAuthHeaders, calculateValidationStatus]);

    // Toggle itemization view
    const toggleItemizationView = useCallback((expenseId) => {
        setExpandedItemized(prev => {
            const newSet = new Set(prev);
            if (newSet.has(expenseId)) {
                newSet.delete(expenseId);
            } else {
                newSet.add(expenseId);
            }
            return newSet;
        });
    }, []);

    // Apply expense account to all line items
    const applyExpenseAccountToAllItems = useCallback(async () => {
        if (!applyToAllExpense || !selectedAccountForAll) return;

        try {
            const selectedAccount = expenseAccounts.find(acc => acc.account_id === selectedAccountForAll);
            if (!selectedAccount) {
                alert('Please select a valid expense account');
                return;
            }

            const response = await fetch(`${API_BASE_URL}/expense-integration/expenses/${applyToAllExpense.id}/apply-expense-account-to-all`, {
                method: 'PUT',
                headers: getAuthHeaders,
                body: JSON.stringify({
                    expense_account_id: selectedAccount.account_id,
                    expense_account_name: selectedAccount.account_name
                })
            });

            const data = await response.json();
            if (data.success) {
                alert(`✅ Applied "${selectedAccount.account_name}" to all ${data.updated_items.length} line items!`);
                setShowApplyToAllModal(false);
                setApplyToAllExpense(null);
                setSelectedAccountForAll('');
                await fetchExpenses();
            } else {
                alert(`❌ Failed to apply account: ${data.message}`);
            }
        } catch (error) {
            console.error('Error applying expense account to all items:', error);
            alert('Error applying expense account: ' + error.message);
        }
    }, [applyToAllExpense, selectedAccountForAll, expenseAccounts, getAuthHeaders, fetchExpenses]);

    // Add this function to your CompleteExpenseManager component
    const handleDeleteLineItem = useCallback(async (expenseId, itemIndex) => {
        if (!confirm('Are you sure you want to delete this line item? This action cannot be undone.')) {
            return;
        }

        try {
            const response = await fetch(`${API_BASE_URL}/expense-integration/expenses/${expenseId}/line-items/${itemIndex}`, {
                method: 'DELETE',
                headers: getAuthHeaders
            });

            const data = await response.json();
            if (data.success) {
                // Update the local state
                setExpenses(prev => prev.map(exp => {
                    if (exp.id === expenseId) {
                        const newLineItems = exp.line_items_data.filter((_, idx) => idx !== itemIndex);

                        const updatedExpense = {
                            ...exp,
                            line_items_data: newLineItems,
                            has_multiple_items: newLineItems.length > 1 ? 1 : 0,
                            item_count: newLineItems.length
                        };

                        // Recalculate validation status
                        const validationStatus = calculateValidationStatus(updatedExpense);
                        updatedExpense.validationStatus = validationStatus;
                        updatedExpense.integration_ready = validationStatus.isValid && !updatedExpense.zoho_expense_id;

                        return updatedExpense;
                    }
                    return exp;
                }));

                alert(`Line item deleted successfully. ${data.remaining_items} items remaining.`);
            } else {
                alert(`Failed to delete line item: ${data.message}`);
            }
        } catch (error) {
            console.error('Error deleting line item:', error);
            alert('Error deleting line item: ' + error.message);
        }
    }, [getAuthHeaders, calculateValidationStatus]);

    // Sync selected expenses - FIXED GST HANDLING
    const syncSelectedExpenses = useCallback(async () => {
        if (!expenses || !Array.isArray(expenses)) {
            alert('No expenses data available');
            return;
        }

        // Filter for valid expenses that can be synced (approved only)
        const validExpenses = expenses.filter(e => {
            const isSelected = selectedExpenses.includes(e.id);
            const isValidForSync = e.validationStatus?.isValid;
            const canSyncExpense = canSync(e);

            return isSelected && isValidForSync && canSyncExpense;
        });

        // Check if any selected expenses are not approved
        const unapprovedSelected = expenses.filter(e =>
            selectedExpenses.includes(e.id) && !canSync(e)
        );

        if (unapprovedSelected.length > 0) {
            const statusCounts = unapprovedSelected.reduce((acc, exp) => {
                const status = exp.approver_status || 'unknown';
                acc[status] = (acc[status] || 0) + 1;
                return acc;
            }, {});

            let message = 'Cannot sync the following expenses:\n';
            Object.entries(statusCounts).forEach(([status, count]) => {
                const statusText = status === 'pending_approval' ? 'Pending Approval'
                    : status === 'rejected' ? 'Rejected'
                        : 'Unknown Status';
                message += `• ${count} expense(s) with ${statusText}\n`;
            });
            message += '\nOnly approved expenses can be synced.';

            alert(message);
            return;
        }

        if (validExpenses.length === 0) {
            alert('No valid expenses selected for sync');
            return;
        }

        setSyncInProgress(true);

        try {
            const expenseUpdates = validExpenses.map(expense => {
                const baseExpenseData = {
                    expense_id: expense.id,
                    payment_account_id: expense.zoho_payment_account_id,
                    payment_account_name: expense.zoho_payment_account_name,
                    amount: expense.amount,
                    total: expense.total,
                    description: expense.items,
                    reference_number: expense.invoice_no,
                    expense_date: expense.date,
                    zoho_vendor_id: expense.zoho_vendor_id,
                    vendor_name: expense.vendor_name,
                    vendor_gstin: expense.vendor_gstin || expense.gstin,
                    gstin: expense.gstin,
                    gst_treatment: expense.gst_treatment,
                    cgst_rate: expense.cgst_rate,
                    sgst_rate: expense.sgst_rate,
                    igst_rate: expense.igst_rate,
                    total_tax: expense.total_tax_amount,
                    is_tax_inclusive: expense.is_tax_inclusive,
                    vendor_type: expense.vendor_type,
                    company_id: currentOrganization.organization_id
                };

                // Add itemization data from safe parsed structure
                if (expense.has_multiple_items && expense.line_items_data) {
                    const lineItems = Array.isArray(expense.line_items_data)
                        ? expense.line_items_data
                        : [];

                    baseExpenseData.is_itemized = true;
                    baseExpenseData.line_items = lineItems.map(item => ({
                        description: item.description || item.name,
                        amount: item.amount,
                        expense_account_id: item.expense_account_id,
                        expense_account_name: item.expense_account_name,
                        cgst_rate: item.cgst_rate || 0,
                        sgst_rate: item.sgst_rate || 0,
                        igst_rate: item.igst_rate || 0,
                        cgst_amount: item.cgst_amount || 0,
                        sgst_amount: item.sgst_amount || 0,
                        igst_amount: item.igst_amount || 0,
                        total_tax: item.total_tax || 0,
                        total_with_tax: item.total_with_tax || item.amount,
                        itc_eligibility: item.itc_eligibility || 'eligible'
                    }));
                } else {
                    baseExpenseData.is_itemized = false;
                    baseExpenseData.expense_account_id = expense.zoho_expense_account_id;
                    baseExpenseData.expense_account_name = expense.zoho_expense_account_name;
                }

                return baseExpenseData;
            });

            const response = await fetch(`${API_BASE_URL}/expense-integration/expenses/sync-to-zoho`, {
                method: 'POST',
                headers: getAuthHeaders,
                body: JSON.stringify({ expenseUpdates })
            });

            const data = await response.json();
            if (data.success) {
                await fetchExpenses();

                const successfulIds = (data.results || []).filter(r => r.success).map(r => r.expense_id);
                setSelectedExpenses(prev => prev.filter(id => !successfulIds.includes(id)));

                const itemizedCount = data.results?.filter(r => r.success && r.is_itemized).length || 0;
                const singleCount = data.results?.filter(r => r.success && !r.is_itemized).length || 0;

                // Show GST info in success message
                const gstInfo = data.results?.filter(r => r.success).map(r =>
                    `${r.expense_id}: ${r.gst_treatment || 'No GST'} ${r.vendor_gstin ? `(${r.vendor_gstin})` : ''}`
                ).join('\n');

                alert(`Sync completed! ${singleCount} single expenses and ${itemizedCount} itemized expenses synced successfully.\n\nGST Details:\n${gstInfo}`);
            } else {
                alert('Sync failed: ' + data.message);
            }
        } catch (error) {
            console.error('Sync error:', error);
            alert('Sync error: ' + error.message);
        } finally {
            setSyncInProgress(false);
        }
    }, [expenses, selectedExpenses, canSync, currentOrganization.organization_id, getAuthHeaders, fetchExpenses]);

    // Utility functions
    const formatCurrency = useCallback((amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR'
        }).format(amount || 0);
    }, []);

    const getStatusIcon = useCallback((expense) => {
        if (expense.zoho_expense_id) return <CheckCircle className="w-5 h-5 text-green-500" />;
        if (expense.zoho_sync_status === 'failed') return <XCircle className="w-5 h-5 text-red-500" />;
        if (expense.validationStatus?.isValid) return <Clock className="w-5 h-5 text-blue-500" />;
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
    }, []);

    const getStatusText = useCallback((expense) => {
        if (expense.zoho_expense_id) return 'Synced to Zoho';
        if (expense.zoho_sync_status === 'failed') return 'Sync Failed';
        if (expense.validationStatus?.isValid) return 'Ready for Sync';
        return 'Validation Required';
    }, []);

    const getCompletionColor = useCallback((percentage) => {
        if (percentage >= 100) return 'bg-green-500';
        if (percentage >= 75) return 'bg-blue-500';
        if (percentage >= 50) return 'bg-yellow-500';
        return 'bg-red-500';
    }, []);

    // Get bank validation badge
    const getBankValidationBadge = useCallback((expense) => {
        if (!expense.bank_name || !expense.bank_account_number) {
            return (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                    No Bank Details
                </span>
            );
        }

        if (!expense.zoho_payment_account_id) {
            return (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    Not Mapped: {expense.bank_name} ****{expense.bank_account_number.toString().slice(-4)}
                </span>
            );
        }

        return (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                Mapped: {expense.zoho_payment_account_name}
            </span>
        );
    }, []);

    // Memoized filtered expenses
    const filteredExpenses = useMemo(() => {
        if (!expenses || !Array.isArray(expenses)) return [];

        return expenses.filter(expense => {
            if (filter === 'ready') return expense.validationStatus?.isValid && !expense.zoho_expense_id;
            if (filter === 'synced') return expense.zoho_expense_id;
            if (filter === 'failed') return expense.zoho_sync_status === 'failed';
            if (filter === 'pending') return !expense.validationStatus?.isValid && !expense.zoho_expense_id;
            if (filter === 'itemized') return expense.has_multiple_items;
            if (filter === 'bank_unmapped') return expense.bank_name && !expense.zoho_payment_account_id;
            return true;
        });
    }, [expenses, filter]);

    // Memoized stats
    const stats = useMemo(() => {
        if (!expenses || !Array.isArray(expenses)) {
            return { total: 0, ready: 0, synced: 0, failed: 0, pending: 0, itemized: 0, bank_unmapped: 0 };
        }

        return {
            total: expenses.length,
            ready: expenses.filter(e => e.validationStatus?.isValid && !e.zoho_expense_id).length,
            synced: expenses.filter(e => e.zoho_expense_id).length,
            failed: expenses.filter(e => e.zoho_sync_status === 'failed').length,
            pending: expenses.filter(e => !e.validationStatus?.isValid && !e.zoho_expense_id).length,
            itemized: expenses.filter(e => e.has_multiple_items).length,
            bank_unmapped: expenses.filter(e => e.bank_name && !e.zoho_payment_account_id).length
        };
    }, [expenses]);

    // Initialize edit form data
    useEffect(() => {
        if (editingExpense) {
            setEditFormData({
                date: editingExpense.date ? editingExpense.date.split('T')[0] : '',
                invoice_no: editingExpense.invoice_no || '',
                total: editingExpense.total || '',
                vendor_name: editingExpense.vendor_name || '',
                gstin: editingExpense.gstin || '',
                items: editingExpense.items || '',
                zoho_payment_account_id: editingExpense.zoho_payment_account_id || '',
                zoho_expense_account_id: editingExpense.zoho_expense_account_id || ''
            });
        }
    }, [editingExpense]);

    // Enhanced Itemization Display Component with Full Editing Capabilities
    const ItemizationDisplay = ({ expense, isExpanded }) => {
        if (!expense.has_multiple_items || !expense.line_items_data) {
            return null;
        }

        // Check if user can view itemization
        if (!canViewItemization(expense)) {
            return (
                <div className="mt-3 border-t pt-3">
                    <div className="text-center py-4">
                        <AlertTriangle className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
                        <p className="text-sm text-gray-600">
                            Itemization view blocked: {
                                expense.approver_status === 'pending_approval'
                                    ? 'Expense is pending approval'
                                    : expense.approver_status === 'rejected'
                                        ? 'Expense has been rejected'
                                        : 'Expense is not approved'
                            }
                        </p>
                    </div>
                </div>
            );
        }

        const items = Array.isArray(expense.line_items_data)
            ? expense.line_items_data
            : [];

        if (items.length === 0) {
            return (
                <div className="mt-3 border-t pt-3">
                    <div className="text-center py-4">
                        <AlertTriangle className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
                        <p className="text-sm text-gray-600">Line items data is corrupt or empty</p>
                        <button
                            onClick={() => fixLineItemsData(expense.id)}
                            className="mt-2 px-3 py-1 bg-yellow-100 text-yellow-800 rounded text-xs hover:bg-yellow-200"
                        >
                            Fix Line Items Data
                        </button>
                    </div>
                </div>
            );
        }

        const handleAiSuggestionClick = async (itemIndex, suggestion) => {
            const success = await updateLineItemAccount(expense.id, itemIndex, {
                expense_account_id: suggestion.account_id,
                expense_account_name: suggestion.account_name
            });

            if (success) {
                // Clear AI suggestions for this item after successful selection
                const suggestionKey = `${expense.id}-${itemIndex}`;
                setAiSuggestions(prev => ({
                    ...prev,
                    [suggestionKey]: []
                }));
            }
        };

        // Handle line item field changes with tax calculation
        const handleLineItemChange = async (itemIndex, field, value) => {
            const item = items[itemIndex];
            const updatedItem = { ...item, [field]: value };

            // If rate or quantity changed, recalculate amounts
            if (field === 'rate' || field === 'quantity') {
                const calculations = calculateTaxAmounts(
                    updatedItem.rate || item.rate,
                    updatedItem.quantity || item.quantity,
                    item.cgst_rate,
                    item.sgst_rate,
                    item.igst_rate
                );
                Object.assign(updatedItem, calculations);
            }

            // If tax rates changed, recalculate tax amounts
            if (field === 'cgst_rate' || field === 'sgst_rate' || field === 'igst_rate') {
                const calculations = calculateTaxAmounts(
                    item.rate,
                    item.quantity,
                    updatedItem.cgst_rate || item.cgst_rate,
                    updatedItem.sgst_rate || item.sgst_rate,
                    updatedItem.igst_rate || item.igst_rate
                );
                Object.assign(updatedItem, calculations);
            }

            await updateLineItemAccount(expense.id, itemIndex, updatedItem);
        };

        return (
            <div className="mt-3 border-t pt-3">
                <div
                    className="flex items-center gap-2 cursor-pointer text-blue-600 hover:text-blue-800"
                    onClick={() => toggleItemizationView(expense.id)}
                >
                    <Package className="w-4 h-4" />
                    <span className="text-sm font-medium">
                        {items.length} Line Items • Total: {formatCurrency(items.reduce((sum, item) => sum + (item.amount || 0), 0))}
                    </span>
                    {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </div>

                {isExpanded && (
                    <div className="mt-3 space-y-3">
                        {/* Apply to All Button */}
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <CheckCircle2 className="w-4 h-4 text-blue-600" />
                                    <span className="text-sm font-medium text-blue-900">
                                        Apply Same Expense Account to All Items
                                    </span>
                                </div>
                                <button
                                    onClick={() => {
                                        setApplyToAllExpense(expense);
                                        setShowApplyToAllModal(true);
                                    }}
                                    className="px-3 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                                >
                                    Apply to All
                                </button>
                            </div>
                        </div>

                        {items.map((item, index) => {
                            const suggestionKey = `${expense.id}-${index}`;
                            const itemSuggestions = aiSuggestions[suggestionKey] || [];
                            const isLoadingSuggestions = aiSuggestionsLoading[suggestionKey];
                            const isEditingThis = editingLineItem === `${expense.id}-${index}`;

                            return (
                                // Add this key to the entire line item container div
                                <div key={`${expense.id}-${index}-${item.expense_account_id}-${item.itc_eligibility}`} className="bg-gray-50 border rounded-lg p-4">
                                    <div className="flex items-start justify-between mb-3">
                                        <div className="flex-1">
                                            <div className="font-medium text-sm text-gray-900">
                                                {item.name || item.description}
                                            </div>
                                            <div className="text-xs text-gray-600 mt-1">
                                                Line Item #{index + 1}
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="font-bold text-sm text-gray-900">
                                                {formatCurrency(item.amount)}
                                            </div>
                                            {item.total_with_tax && item.total_with_tax !== item.amount && (
                                                <div className="text-xs text-green-600">
                                                    With Tax: {formatCurrency(item.total_with_tax)}
                                                </div>
                                            )}
                                        </div>
                                        {/* Action buttons section - Update this part in ItemizationDisplay */}
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => {
                                                    if (!canEdit(expense)) {
                                                        alert(`Cannot edit: ${expense.approver_status === 'pending_approval'
                                                            ? 'Expense is pending approval'
                                                            : expense.approver_status === 'rejected'
                                                                ? 'Expense has been rejected'
                                                                : 'Expense is not approved'
                                                            }`);
                                                        return;
                                                    }

                                                    if (isEditingThis) {
                                                        setEditingLineItem(null);
                                                    } else {
                                                        setEditingLineItem(`${expense.id}-${index}`);
                                                        setLineItemEditData(item);
                                                    }
                                                }}
                                                disabled={!canEdit(expense) || !!expense.zoho_expense_id}
                                                className={`p-1 ${!canEdit(expense) || expense.zoho_expense_id
                                                    ? 'text-gray-400 cursor-not-allowed'
                                                    : 'text-blue-600 hover:text-blue-800'
                                                    }`}
                                                title={
                                                    !canEdit(expense)
                                                        ? `Cannot edit: ${expense.approver_status === 'pending_approval'
                                                            ? 'Pending approval'
                                                            : expense.approver_status === 'rejected'
                                                                ? 'Rejected'
                                                                : 'Not approved'}`
                                                        : expense.zoho_expense_id
                                                            ? "Cannot edit synced expense"
                                                            : (isEditingThis ? "Save changes" : "Edit line item")
                                                }
                                            >
                                                {isEditingThis ? <Save className="w-4 h-4" /> : <Edit className="w-4 h-4" />}
                                            </button>

                                            {/* DELETE BUTTON - Add this inside ItemizationDisplay */}
                                            {!expense.zoho_expense_id && items.length > 1 && (
                                                <button
                                                    onClick={() => handleDeleteLineItem(expense.id, index)}
                                                    className="p-1 text-red-600 hover:text-red-800"
                                                    title="Delete line item"
                                                >
                                                    <X className="w-4 h-4" />
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    {/* Enhanced Editing Mode */}
                                    {isEditingThis && (
                                        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
                                            <div className="grid grid-cols-2 gap-3 mb-3">
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-600 mb-1">
                                                        Rate (₹)
                                                    </label>
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        value={lineItemEditData.rate || ''}
                                                        onChange={(e) => setLineItemEditData(prev => ({ ...prev, rate: e.target.value }))}
                                                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-600 mb-1">
                                                        Quantity
                                                    </label>
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        value={lineItemEditData.quantity || 1}
                                                        onChange={(e) => setLineItemEditData(prev => ({ ...prev, quantity: e.target.value }))}
                                                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                                                    />
                                                </div>
                                            </div>

                                            {/* Tax Rates */}
                                            <div className="grid grid-cols-3 gap-2 mb-3">
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-600 mb-1">
                                                        CGST (%)
                                                    </label>
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        value={lineItemEditData.cgst_rate || 0}
                                                        onChange={(e) => setLineItemEditData(prev => ({ ...prev, cgst_rate: e.target.value }))}
                                                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-600 mb-1">
                                                        SGST (%)
                                                    </label>
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        value={lineItemEditData.sgst_rate || 0}
                                                        onChange={(e) => setLineItemEditData(prev => ({ ...prev, sgst_rate: e.target.value }))}
                                                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-600 mb-1">
                                                        IGST (%)
                                                    </label>
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        value={lineItemEditData.igst_rate || 0}
                                                        onChange={(e) => setLineItemEditData(prev => ({ ...prev, igst_rate: e.target.value }))}
                                                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                                                    />
                                                </div>
                                            </div>

                                            {/* ITC Eligibility */}
                                            <div className="mb-3">
                                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                                    ITC Eligibility *
                                                </label>
                                                <select
                                                    value={lineItemEditData.itc_eligibility || 'eligible'}
                                                    onChange={(e) => setLineItemEditData(prev => ({ ...prev, itc_eligibility: e.target.value }))}
                                                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                                                >
                                                    {itcOptions.map(option => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.label} - {option.description}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={async () => {
                                                        const calculations = calculateTaxAmounts(
                                                            lineItemEditData.rate,
                                                            lineItemEditData.quantity,
                                                            lineItemEditData.cgst_rate,
                                                            lineItemEditData.sgst_rate,
                                                            lineItemEditData.igst_rate
                                                        );

                                                        const finalData = { ...lineItemEditData, ...calculations };

                                                        await updateLineItemAccount(expense.id, index, finalData);
                                                        setEditingLineItem(null);
                                                        setLineItemEditData({});
                                                    }}
                                                    className="px-3 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700"
                                                >
                                                    <Calculator className="w-3 h-3 inline mr-1" />
                                                    Calculate & Save
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setEditingLineItem(null);
                                                        setLineItemEditData({});
                                                    }}
                                                    className="px-3 py-1 bg-gray-100 text-gray-700 rounded text-xs hover:bg-gray-200"
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {/* Expense account selection with AI suggestions */}
                                    <div className="mb-3">
                                        <div className="flex items-center justify-between mb-1">
                                            <label className="block text-xs font-medium text-gray-600">
                                                Expense Account *
                                            </label>
                                            <button
                                                onClick={() => getAiExpenseAccountSuggestions(
                                                    item.description || item.name,
                                                    expense.vendor_name,
                                                    item.amount,
                                                    expense.id,
                                                    index,
                                                    true // Enable auto-select
                                                )}
                                                disabled={isLoadingSuggestions}
                                                className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-800 disabled:opacity-50"
                                            >
                                                <Settings className="w-3 h-3" />
                                                {isLoadingSuggestions ? 'Auto-selecting...' : 'AI Auto-Select'}
                                            </button>
                                        </div>

                                        {/* AI Suggestions - FIXED */}
                                        {itemSuggestions.length > 0 && (
                                            <div className="mb-2 p-2 bg-purple-50 border border-purple-200 rounded">
                                                <div className="text-xs font-medium text-purple-800 mb-1 flex items-center gap-1">
                                                    <Settings className="w-3 h-3" />
                                                    Alternative AI Suggestions
                                                </div>
                                                <div className="space-y-1">
                                                    {itemSuggestions.slice(0, 3).map((suggestion, idx) => (
                                                        <button
                                                            key={idx}
                                                            onClick={() => handleAiSuggestionClick(index, suggestion)}
                                                            className="block w-full text-left px-2 py-1 text-xs bg-white border border-purple-200 rounded hover:bg-purple-50"
                                                        >
                                                            <div className="font-medium">{suggestion.account_name}</div>
                                                            <div className="text-purple-600">{suggestion.reason}</div>
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        <select
                                            value={item.expense_account_id || ''}
                                            onChange={async (e) => {
                                                const selectedAccount = expenseAccounts.find(acc => acc.account_id === e.target.value);
                                                console.log('🎯 Manual account selection:', selectedAccount);

                                                const success = await updateLineItemAccount(expense.id, index, {
                                                    expense_account_id: e.target.value,
                                                    expense_account_name: selectedAccount?.account_name || ''
                                                });

                                                if (success) {
                                                    // Force immediate re-render by updating the parent state
                                                    const updatedExpense = expenses.find(exp => exp.id === expense.id);
                                                    if (updatedExpense) {
                                                        const newLineItems = [...updatedExpense.line_items_data];
                                                        newLineItems[index] = {
                                                            ...newLineItems[index],
                                                            expense_account_id: e.target.value,
                                                            expense_account_name: selectedAccount?.account_name || ''
                                                        };

                                                        setExpenses(prev => prev.map(exp =>
                                                            exp.id === expense.id
                                                                ? { ...exp, line_items_data: newLineItems }
                                                                : exp
                                                        ));
                                                    }
                                                }
                                            }}
                                            disabled={!!expense.zoho_expense_id}
                                            className={`w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 ${expense.zoho_expense_id ? 'bg-gray-100 cursor-not-allowed' : ''
                                                }`}
                                        >
                                            <option value="">Select Account</option>
                                            {expenseAccounts.map(account => (
                                                <option key={account.account_id} value={account.account_id}>
                                                    {account.account_name}
                                                </option>
                                            ))}
                                        </select>
                                        {/* Current selection display */}
                                        <div className="mt-1 text-xs">
                                            {item.expense_account_id ? (
                                                <div className="text-green-600 bg-green-50 px-2 py-1 rounded border-l-2 border-green-400">
                                                    Current: {item.expense_account_name}
                                                    <div className="text-gray-500 text-xs">ID: {item.expense_account_id}</div>
                                                </div>
                                            ) : (
                                                <div className="text-red-600 bg-red-50 px-2 py-1 rounded border-l-2 border-red-400">
                                                    No account selected
                                                </div>
                                            )}
                                        </div>
                                        {item.expense_account_name && (
                                            <div className="text-xs text-green-600 mt-1">
                                                ✓ {item.expense_account_name}
                                            </div>
                                        )}
                                    </div>

                                    {/* ITC Eligibility Display */}
                                    <div className="mb-3">
                                        <label className="block text-xs font-medium text-gray-600 mb-1">
                                            ITC Eligibility
                                        </label>
                                        <select
                                            value={item.itc_eligibility || 'eligible'}
                                            onChange={async (e) => {
                                                await updateLineItemAccount(expense.id, index, {
                                                    itc_eligibility: e.target.value
                                                });
                                            }}
                                            disabled={!!expense.zoho_expense_id}
                                            className={`w-full px-2 py-1 text-xs border border-gray-300 rounded ${expense.zoho_expense_id ? 'bg-gray-100 cursor-not-allowed' : ''
                                                }`}
                                        >
                                            {itcOptions.map(option => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                        {/* Current selection display */}
                                        <div className="mt-1 text-xs">
                                            <div className={`px-2 py-1 rounded border-l-2 ${item.itc_eligibility === 'eligible'
                                                ? 'text-green-600 bg-green-50 border-green-400'
                                                : 'text-orange-600 bg-orange-50 border-orange-400'
                                                }`}>
                                                Current: {itcOptions.find(opt => opt.value === (item.itc_eligibility || 'eligible'))?.label || 'Eligible for ITC'}
                                            </div>
                                        </div>
                                        <div className="text-xs text-gray-500 mt-1">
                                            {itcOptions.find(opt => opt.value === (item.itc_eligibility || 'eligible'))?.description}
                                        </div>
                                    </div>

                                    {/* Tax information display */}
                                    {(item.cgst_rate > 0 || item.sgst_rate > 0 || item.igst_rate > 0) && (
                                        <div className="bg-white border rounded p-2">
                                            <div className="text-xs font-medium text-gray-700 mb-2">
                                                Tax Details - GST {((item.cgst_rate || 0) + (item.sgst_rate || 0) + (item.igst_rate || 0))}%
                                                <span className={`ml-2 px-1 rounded text-xs ${item.itc_eligibility === 'eligible'
                                                    ? 'bg-green-100 text-green-700'
                                                    : 'bg-orange-100 text-orange-700'
                                                    }`}>
                                                    {item.itc_eligibility === 'eligible' ? 'ITC Eligible' :
                                                        item.itc_eligibility === 'ineligible_section17' ? 'ITC Blocked (Food)' : 'ITC Ineligible'}
                                                </span>
                                            </div>
                                            <div className="grid grid-cols-1 gap-2 text-xs">
                                                {item.cgst_rate > 0 && (
                                                    <div className="flex justify-between">
                                                        <span>CGST ({item.cgst_rate}%):</span>
                                                        <span>{formatCurrency(item.cgst_amount || 0)}</span>
                                                    </div>
                                                )}
                                                {item.sgst_rate > 0 && (
                                                    <div className="flex justify-between">
                                                        <span>SGST ({item.sgst_rate}%):</span>
                                                        <span>{formatCurrency(item.sgst_amount || 0)}</span>
                                                    </div>
                                                )}
                                                {item.igst_rate > 0 && (
                                                    <div className="flex justify-between">
                                                        <span>IGST ({item.igst_rate}%):</span>
                                                        <span>{formatCurrency(item.igst_amount || 0)}</span>
                                                    </div>
                                                )}
                                                {item.total_tax > 0 && (
                                                    <div className="border-t pt-1 mt-1 flex justify-between font-medium">
                                                        <span>Total Tax:</span>
                                                        <span>{formatCurrency(item.total_tax)}</span>
                                                    </div>
                                                )}
                                                <div className="border-t pt-1 mt-1 flex justify-between text-green-600 font-medium">
                                                    <span>Amount + Tax:</span>
                                                    <span>{formatCurrency(item.total_with_tax || (item.amount + (item.total_tax || 0)))}</span>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Validation Status */}
                                    <div className="mt-2 flex items-center gap-2">
                                        {item.expense_account_id ? (
                                            <div className="flex items-center gap-1 text-xs text-green-600">
                                                <CheckCircle className="w-3 h-3" />
                                                Ready for sync
                                            </div>
                                        ) : (
                                            <div className="flex items-center gap-1 text-xs text-red-600">
                                                <AlertCircle className="w-3 h-3" />
                                                Expense account required
                                            </div>
                                        )}

                                        {/* Quick ITC Status Indicator */}
                                        <div className={`flex items-center gap-1 text-xs ${item.itc_eligibility === 'eligible'
                                            ? 'text-green-600'
                                            : 'text-orange-600'
                                            }`}>
                                            <Percent className="w-3 h-3" />
                                            {item.itc_eligibility === 'eligible' ? 'ITC OK' : 'ITC Blocked'}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="max-w-7xl mx-auto p-6 space-y-6">
            {/* Header */}
            <div className="bg-white rounded-lg border p-6">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">Complete Expense Manager</h1>
                        <p className="text-gray-600 mt-2">
                            ✅ ENHANCED VERSION - Full Line Item Editing • Rate/Quantity/Tax Calculation • ITC Eligibility • AI Suggestions Fixed
                        </p>
                        <p className="text-gray-600 mt-1">
                            Organization: <span className="font-medium text-blue-600">{currentOrganization.name}</span>
                            {" "}({currentOrganization.organization_id})
                        </p>
                    </div>
                    <button
                        onClick={forceRefresh}
                        disabled={loading}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                        {loading ? 'Loading...' : 'Refresh Data'}
                    </button>
                </div>

                {/* Enhanced Stats Dashboard */}
                <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center gap-3">
                            <Receipt className="w-8 h-8 text-gray-600" />
                            <div>
                                <h3 className="text-2xl font-bold text-gray-900">{stats.total}</h3>
                                <p className="text-sm text-gray-600">Total Expenses</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex items-center gap-3">
                            <Clock className="w-8 h-8 text-blue-600" />
                            <div>
                                <h3 className="text-2xl font-bold text-blue-900">{stats.ready}</h3>
                                <p className="text-sm text-blue-600">Ready to Sync</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex items-center gap-3">
                            <CheckCircle className="w-8 h-8 text-green-600" />
                            <div>
                                <h3 className="text-2xl font-bold text-green-900">{stats.synced}</h3>
                                <p className="text-sm text-green-600">Synced</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                        <div className="flex items-center gap-3">
                            <Package className="w-8 h-8 text-purple-600" />
                            <div>
                                <h3 className="text-2xl font-bold text-purple-900">{stats.itemized}</h3>
                                <p className="text-sm text-purple-600">Itemized</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <div className="flex items-center gap-3">
                            <XCircle className="w-8 h-8 text-red-600" />
                            <div>
                                <h3 className="text-2xl font-bold text-red-900">{stats.failed}</h3>
                                <p className="text-sm text-red-600">Failed</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                        <div className="flex items-center gap-3">
                            <AlertTriangle className="w-8 h-8 text-orange-600" />
                            <div>
                                <h3 className="text-2xl font-bold text-orange-900">{stats.bank_unmapped}</h3>
                                <p className="text-sm text-orange-600">Bank Unmapped</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters and Actions */}
            <div className="bg-white rounded-lg border p-6">
                <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                    <div className="flex items-center gap-2">
                        <Filter className="w-5 h-5 text-gray-500" />
                        <div className="flex gap-2 flex-wrap">
                            {['all', 'pending', 'ready', 'synced', 'failed', 'itemized', 'bank_unmapped'].map(filterType => (
                                <button
                                    key={filterType}
                                    onClick={() => setFilter(filterType)}
                                    className={`px-3 py-1 rounded-full text-sm font-medium ${filter === filterType
                                        ? 'bg-blue-100 text-blue-800'
                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                        }`}
                                >
                                    {filterType.charAt(0).toUpperCase() + filterType.slice(1).replace('_', ' ')}
                                    {filterType === 'bank_unmapped' && stats.bank_unmapped > 0 && (
                                        <span className="ml-1 text-xs">({stats.bank_unmapped})</span>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                        {selectedExpenses.length > 0 && (
                            <span className="text-sm text-gray-600">
                                {selectedExpenses.length} selected
                            </span>
                        )}

                        <button
                            onClick={() => {
                                const readyIds = filteredExpenses
                                    .filter(e => e.validationStatus?.isValid && !e.zoho_expense_id)
                                    .map(e => e.id);
                                setSelectedExpenses(readyIds);
                            }}
                            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                        >
                            Select All Ready ({stats.ready})
                        </button>

                        <button
                            onClick={() => setSelectedExpenses([])}
                            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                        >
                            Clear Selection
                        </button>
                    </div>

                    {selectedExpenses.length > 0 && (
                        <button
                            onClick={syncSelectedExpenses}
                            disabled={syncInProgress}
                            className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                        >
                            {syncInProgress ? (
                                <>
                                    <RefreshCw className="w-4 h-4 animate-spin" />
                                    Syncing...
                                </>
                            ) : (
                                <>
                                    <Send className="w-4 h-4" />
                                    Sync {selectedExpenses.length} Expenses
                                </>
                            )}
                        </button>
                    )}
                </div>
            </div>

            {/* Main Expenses Table with itemization display would continue here... */}
            {/* Expenses Table */}
            <div className="bg-white rounded-lg border">
                <div className="p-6 border-b">
                    <h2 className="text-xl font-semibold text-gray-900">Expenses (Enhanced - Full Line Item Editing with ITC)</h2>
                    <p className="text-gray-600 mt-1">
                        Showing {filteredExpenses.length} of {expenses.length} expenses
                    </p>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50 border-b">
                            <tr>
                                <th className="w-12 px-4 py-3 text-left">
                                    <input
                                        type="checkbox"
                                        checked={selectedExpenses.length === filteredExpenses.filter(e => e.validationStatus?.isValid && !e.zoho_expense_id).length && filteredExpenses.filter(e => e.validationStatus?.isValid && !e.zoho_expense_id).length > 0}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                const readyIds = filteredExpenses
                                                    .filter(e => e.validationStatus?.isValid && !e.zoho_expense_id)
                                                    .map(e => e.id);
                                                setSelectedExpenses(readyIds);
                                            } else {
                                                setSelectedExpenses([]);
                                            }
                                        }}
                                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    />
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vendor</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Accounts</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bank Status</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Completion</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Approval Status</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {filteredExpenses.map((expense) => {
                                const validationStatus = calculateValidationStatus(expense);
                                const isItemizedExpanded = expandedItemized.has(expense.id);

                                return (
                                    <React.Fragment key={expense.id}>
                                        <tr className="hover:bg-gray-50">
                                            <td className="px-4 py-3">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedExpenses.includes(expense.id)}
                                                    onChange={(e) => {
                                                        if (!canSync(expense)) {
                                                            alert(`Cannot select expense: ${expense.approver_status === 'pending_approval'
                                                                ? 'Expense is pending approval'
                                                                : expense.approver_status === 'rejected'
                                                                    ? 'Expense has been rejected'
                                                                    : 'Expense is not approved'
                                                                }`);
                                                            return;
                                                        }

                                                        if (e.target.checked) {
                                                            setSelectedExpenses(prev => [...prev, expense.id]);
                                                        } else {
                                                            setSelectedExpenses(prev => prev.filter(id => id !== expense.id));
                                                        }
                                                    }}
                                                    disabled={!canSync(expense) || expense.zoho_expense_id}
                                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                                                />
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-2">
                                                    {getStatusIcon(expense)}
                                                    <span className="text-sm text-gray-900">
                                                        {getStatusText(expense)}
                                                    </span>
                                                    {expense.has_multiple_items && (
                                                        <Package className="w-4 h-4 text-blue-500" title="Multiple Items" />
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-900">
                                                {expense.date ? new Date(expense.date).toLocaleDateString() : 'N/A'}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-900">
                                                <div>
                                                    <div className="font-medium">{expense.vendor_name || 'N/A'}</div>
                                                    {(expense.gstin || expense.vendor_gstin) && expense.gstin !== 'N/A' && (
                                                        <div className="text-xs text-green-600">
                                                            GSTIN: {expense.vendor_gstin || expense.gstin}
                                                            <span className="ml-1 px-1 bg-green-100 rounded text-green-700">
                                                                Registered
                                                            </span>
                                                        </div>
                                                    )}
                                                    {(!expense.gstin || expense.gstin === 'N/A') && !expense.vendor_gstin && (
                                                        <div className="text-xs text-gray-500">
                                                            <span className="px-1 bg-gray-100 rounded">
                                                                Unregistered
                                                            </span>
                                                        </div>
                                                    )}
                                                    {expense.gst_treatment && (
                                                        <div className="text-xs text-blue-600 mt-1">
                                                            GST: {expense.gst_treatment === 'business_gst' ? 'Business GST' :
                                                                expense.gst_treatment === 'consumer' ? 'Consumer' :
                                                                    expense.gst_treatment}
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-900">
                                                <div className="max-w-xs">
                                                    <div className="truncate" title={expense.items}>
                                                        {expense.items || 'N/A'}
                                                    </div>
                                                    {expense.has_multiple_items && expense.line_items_data && (
                                                        <div className="text-xs text-blue-600 mt-1">
                                                            {Array.isArray(expense.line_items_data) ? expense.line_items_data.length : 0} items
                                                        </div>
                                                    )}
                                                    {/* Supporting Document Link */}
                                                    {(() => {
                                                        if (!expense.aws_link || expense.aws_link === '' || expense.aws_link === 'null' || expense.aws_link === 'N/A' || expense.aws_link === 'NULL') {
                                                            return null;
                                                        }

                                                        // Clean the URL - remove "Here is the aws link " prefix if present
                                                        let cleanUrl = expense.aws_link;
                                                        if (cleanUrl.startsWith('Here is the aws link ')) {
                                                            cleanUrl = cleanUrl.replace('Here is the aws link ', '');
                                                        }

                                                        // URL decode if needed
                                                        try {
                                                            cleanUrl = decodeURIComponent(cleanUrl);
                                                        } catch (e) {
                                                            // If decode fails, use original
                                                        }

                                                        // Validate it's a proper URL
                                                        if (!cleanUrl.startsWith('http')) {
                                                            return null;
                                                        }

                                                        return (
                                                            <div className="mt-1">
                                                                <a
                                                                    href={cleanUrl}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 hover:bg-purple-200 transition-colors cursor-pointer"
                                                                    title="View supporting document"
                                                                >
                                                                    <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                                    </svg>
                                                                    View Document
                                                                </a>
                                                            </div>
                                                        );
                                                    })()}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                                {formatCurrency(expense.total)}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-900">
                                                <div className="space-y-1">
                                                    <div>
                                                        <span className="text-xs text-gray-500">Payment:</span>
                                                        <div className="font-medium">
                                                            {expense.zoho_payment_account_name || 'Not mapped'}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <span className="text-xs text-gray-500">Expense:</span>
                                                        <div className="font-medium">
                                                            {expense.has_multiple_items
                                                                ? `${Array.isArray(expense.line_items_data)
                                                                    ? expense.line_items_data.filter(item => item.expense_account_id).length
                                                                    : 0}/${Array.isArray(expense.line_items_data)
                                                                        ? expense.line_items_data.length
                                                                        : 0} items`
                                                                : expense.zoho_expense_account_name || 'Not categorized'
                                                            }
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="space-y-1">
                                                    {getBankValidationBadge(expense)}
                                                    {expense.bank_name && !expense.zoho_payment_account_id && (
                                                        <button
                                                            onClick={() => {
                                                                if (!canValidatePayment(expense)) {
                                                                    alert(`Cannot validate payment: ${expense.approver_status === 'pending_approval'
                                                                            ? 'Expense is pending approval'
                                                                            : expense.approver_status === 'rejected'
                                                                                ? 'Expense has been rejected'
                                                                                : 'Expense is not approved'
                                                                        }`);
                                                                    return;
                                                                }
                                                                validateBankAccount(expense);
                                                            }}
                                                            disabled={!canValidatePayment(expense) || bankValidationInProgress || !!expense.zoho_expense_id}
                                                            className={`text-xs text-red-600 hover:text-red-800 underline ${!canValidatePayment(expense) || expense.zoho_expense_id
                                                                    ? 'opacity-50 cursor-not-allowed'
                                                                    : ''
                                                                }`}
                                                            title={
                                                                !canValidatePayment(expense)
                                                                    ? `Cannot validate: ${expense.approver_status === 'pending_approval'
                                                                        ? 'Pending approval'
                                                                        : expense.approver_status === 'rejected'
                                                                            ? 'Rejected'
                                                                            : 'Not approved'}`
                                                                    : expense.zoho_expense_id
                                                                        ? "Cannot validate synced expense"
                                                                        : bankValidationInProgress
                                                                            ? "Checking..."
                                                                            : "Validate"
                                                            }
                                                        >
                                                            {bankValidationInProgress ? 'Checking...' : 'Validate'}
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-16 bg-gray-200 rounded-full h-2">
                                                        <div
                                                            className={`h-2 rounded-full ${getCompletionColor(validationStatus.completionPercentage)}`}
                                                            style={{ width: `${validationStatus.completionPercentage}%` }}
                                                        ></div>
                                                    </div>
                                                    <span className="text-xs text-gray-600">
                                                        {validationStatus.completionPercentage}%
                                                    </span>
                                                </div>
                                                {validationStatus.missingFields.length > 0 && (
                                                    <div className="mt-1">
                                                        <div className="text-xs text-red-600">
                                                            Missing: {validationStatus.missingFields.slice(0, 2).map(f => f.label).join(', ')}
                                                            {validationStatus.missingFields.length > 2 && ` +${validationStatus.missingFields.length - 2} more`}
                                                        </div>
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-4 py-3">
                                                {getApprovalStatusBadge(expense)}
                                                {expense.rejection_reason && expense.approver_status === 'rejected' && (
                                                    <div className="text-xs text-red-600 mt-1" title={expense.rejection_reason}>
                                                        Reason: {expense.rejection_reason.substring(0, 50)}
                                                        {expense.rejection_reason.length > 50 && '...'}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        onClick={() => {
                                                            if (!canEdit(expense)) {
                                                                alert(`Cannot edit expense: ${expense.approver_status === 'pending_approval'
                                                                    ? 'Expense is pending approval'
                                                                    : expense.approver_status === 'rejected'
                                                                        ? 'Expense has been rejected'
                                                                        : 'Editing not allowed'}`);
                                                                return;
                                                            }
                                                            setEditingExpense(expense);
                                                            setShowEditModal(true);
                                                        }}
                                                        disabled={!canEdit(expense) || !!expense.zoho_expense_id}
                                                        className={`p-1 ${!canEdit(expense) || expense.zoho_expense_id
                                                            ? 'text-gray-400 cursor-not-allowed'
                                                            : 'text-blue-600 hover:text-blue-800'
                                                            }`}
                                                        title={
                                                            !canEdit(expense)
                                                                ? `Cannot edit: ${expense.approver_status === 'pending_approval'
                                                                    ? 'Pending approval'
                                                                    : expense.approver_status === 'rejected'
                                                                        ? 'Rejected'
                                                                        : 'Not approved'}`
                                                                : expense.zoho_expense_id
                                                                    ? "Cannot edit synced expense"
                                                                    : "Edit expense"
                                                        }
                                                    >
                                                        <Edit className="w-4 h-4" />
                                                    </button>

                                                    {expense.has_multiple_items && (
                                                        <button
                                                            onClick={() => {
                                                                if (!canViewItemization(expense)) {
                                                                    alert(`Cannot view itemization: ${expense.approver_status === 'pending_approval'
                                                                            ? 'Expense is pending approval'
                                                                            : expense.approver_status === 'rejected'
                                                                                ? 'Expense has been rejected'
                                                                                : 'Expense is not approved'
                                                                        }`);
                                                                    return;
                                                                }
                                                                toggleItemizationView(expense.id);
                                                            }}
                                                            disabled={!canViewItemization(expense)}
                                                            className={`p-1 ${!canViewItemization(expense)
                                                                    ? 'text-gray-400 cursor-not-allowed'
                                                                    : 'text-purple-600 hover:text-purple-800'
                                                                }`}
                                                            title={
                                                                !canViewItemization(expense)
                                                                    ? `Cannot view items: ${expense.approver_status === 'pending_approval'
                                                                        ? 'Pending approval'
                                                                        : expense.approver_status === 'rejected'
                                                                            ? 'Rejected'
                                                                            : 'Not approved'}`
                                                                    : expense.zoho_expense_id
                                                                        ? "Cannot view items for synced expense"
                                                                        : "View itemization"
                                                            }
                                                        >
                                                            <Split className="w-4 h-4" />
                                                        </button>
                                                    )}

                                                    {expense.has_multiple_items && (
                                                        // Update the apply account to all button
                                                        <button
                                                            onClick={() => {
                                                                if (!canApplyAccountToAll(expense)) {
                                                                    alert(`Cannot apply account to all items: ${expense.approver_status === 'pending_approval'
                                                                            ? 'Expense is pending approval'
                                                                            : expense.approver_status === 'rejected'
                                                                                ? 'Expense has been rejected'
                                                                                : 'Expense is not approved'
                                                                        }`);
                                                                    return;
                                                                }
                                                                setApplyToAllExpense(expense);
                                                                setShowApplyToAllModal(true);
                                                            }}
                                                            disabled={!canApplyAccountToAll(expense) || !!expense.zoho_expense_id}
                                                            className={`p-1 ${!canApplyAccountToAll(expense) || expense.zoho_expense_id
                                                                    ? 'text-gray-400 cursor-not-allowed'
                                                                    : 'text-green-600 hover:text-green-800'
                                                                }`}
                                                            title={
                                                                !canApplyAccountToAll(expense)
                                                                    ? `Cannot apply account: ${expense.approver_status === 'pending_approval'
                                                                        ? 'Pending approval'
                                                                        : expense.approver_status === 'rejected'
                                                                            ? 'Rejected'
                                                                            : 'Not approved'}`
                                                                    : expense.zoho_expense_id
                                                                        ? "Cannot modify synced expense"
                                                                        : "Apply account to all items"
                                                            }
                                                        >
                                                            <CheckCircle2 className="w-4 h-4" />
                                                        </button>

                                                    )}

                                                    {expense.bank_name && !expense.zoho_payment_account_id && !expense.zoho_expense_id && (
                                                        <button
                                                            onClick={() => validateBankAccount(expense)}
                                                            className="text-orange-600 hover:text-orange-800 p-1"
                                                            title="Validate bank account"
                                                        >
                                                            <Zap className="w-4 h-4" />
                                                        </button>
                                                    )}

                                                    {expense.zoho_expense_id && (
                                                        <span className="text-xs text-green-600 font-medium">
                                                            Zoho: {expense.zoho_expense_id}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>

                                        {/* Itemization Row */}
                                        {expense.has_multiple_items && isItemizedExpanded && (
                                            <tr>
                                                <td colSpan="10" className="px-4 py-3 bg-gray-50">
                                                    <ItemizationDisplay
                                                        expense={expenses.find(e => e.id === expense.id) || expense}  // Use the updated expense from state
                                                        isExpanded={isItemizedExpanded}
                                                    />
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {filteredExpenses.length === 0 && (
                    <div className="p-12 text-center">
                        <Receipt className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No expenses found</h3>
                        <p className="text-gray-600">
                            {filter === 'all'
                                ? "No expenses have been loaded yet."
                                : `No expenses match the "${filter}" filter.`
                            }
                        </p>
                    </div>
                )}
            </div>

            {/* Apply to All Modal */}
            {showApplyToAllModal && applyToAllExpense && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
                        <div className="p-6 border-b">
                            <div className="flex items-center justify-between">
                                <h2 className="text-lg font-semibold text-gray-900">
                                    Apply Account to All Items
                                </h2>
                                <button
                                    onClick={() => {
                                        setShowApplyToAllModal(false);
                                        setApplyToAllExpense(null);
                                        setSelectedAccountForAll('');
                                    }}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    <X className="w-6 h-6" />
                                </button>
                            </div>
                        </div>

                        <div className="p-6">
                            <div className="mb-4">
                                <p className="text-sm text-gray-600 mb-2">
                                    Apply the same expense account to all {Array.isArray(applyToAllExpense.line_items_data) ? applyToAllExpense.line_items_data.length : 0} line items in expense #{applyToAllExpense.id}
                                </p>
                                <p className="font-medium text-gray-900">{applyToAllExpense.vendor_name}</p>
                            </div>

                            <div className="mb-6">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Select Expense Account *
                                </label>
                                <select
                                    value={selectedAccountForAll}
                                    onChange={(e) => setSelectedAccountForAll(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="">Choose an account...</option>
                                    {expenseAccounts.map(account => (
                                        <option key={account.account_id} value={account.account_id}>
                                            {account.account_name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="flex items-center gap-3">
                                <button
                                    onClick={() => {
                                        setShowApplyToAllModal(false);
                                        setApplyToAllExpense(null);
                                        setSelectedAccountForAll('');
                                    }}
                                    className="flex-1 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={applyExpenseAccountToAllItems}
                                    disabled={!selectedAccountForAll}
                                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Apply to All Items
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Modal */}
            {showEditModal && editingExpense && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[95vh] overflow-y-auto">
                        <div className="p-6 border-b">
                            <div className="flex items-center justify-between">
                                <h2 className="text-xl font-semibold text-gray-900">
                                    Edit Expense #{editingExpense.id}
                                    {editingExpense.has_multiple_items && (
                                        <span className="ml-2 px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
                                            {Array.isArray(editingExpense.line_items_data) ? editingExpense.line_items_data.length : 0} Items
                                        </span>
                                    )}
                                </h2>
                                <button
                                    onClick={() => {
                                        setShowEditModal(false);
                                        setEditingExpense(null);
                                        setEditFormData({});
                                    }}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    <X className="w-6 h-6" />
                                </button>
                            </div>
                        </div>

                        <div className="p-6 space-y-6">
                            {/* ADD THE WARNING HERE - RIGHT AT THE START OF THE MODAL CONTENT */}
                            {editingExpense.zoho_expense_id && (
                                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                                    <div className="flex items-center gap-2">
                                        <CheckCircle className="w-5 h-5 text-green-600" />
                                        <div>
                                            <h3 className="font-medium text-green-900">Expense Already Synced</h3>
                                            <p className="text-sm text-green-700">
                                                This expense has been synced to Zoho (ID: {editingExpense.zoho_expense_id}).
                                                No further modifications are allowed.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {/* Itemization Display in Modal */}
                            {editingExpense.has_multiple_items && (
                                <div className="bg-gray-50 border rounded-lg p-4">
                                    <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center gap-2">
                                        <Split className="w-5 h-5" />
                                        Line Items (Enhanced Editing with ITC)
                                    </h3>
                                    <ItemizationDisplay
                                        expense={expenses.find(e => e.id === editingExpense.id) || editingExpense}
                                        isExpanded={true}
                                    />
                                </div>
                            )}

                            {/* Account Mapping */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Payment Account *</label>
                                    <select
                                        value={editFormData.zoho_payment_account_id || ''}
                                        onChange={(e) => {
                                            const selectedAccount = paymentAccounts.find(acc => acc.account_id === e.target.value);
                                            handleFormDataChange('zoho_payment_account_id', e.target.value);
                                            handleFormDataChange('zoho_payment_account_name', selectedAccount?.account_name || '');
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="">Select Payment Account</option>
                                        {paymentAccounts.map(account => (
                                            <option key={account.account_id} value={account.account_id}>
                                                {account.account_name} ({account.account_type})
                                            </option>
                                        ))}
                                    </select>
                                    {editingExpense.bank_name && (
                                        <div className="mt-2 text-xs text-blue-600">
                                            Bank: {editingExpense.bank_name} ****{editingExpense.bank_account_number?.toString().slice(-4)}
                                        </div>
                                    )}
                                </div>

                                {!editingExpense.has_multiple_items && (
                                    <div>
                                        <div className="flex items-center justify-between mb-2">
                                            <label className="block text-sm font-medium text-gray-700">Expense Account *</label>
                                            <button
                                                onClick={() => getAiExpenseAccountSuggestions(
                                                    editingExpense.items,
                                                    editingExpense.vendor_name,
                                                    editingExpense.total,
                                                    editingExpense.id,
                                                    null,
                                                    true // Enable auto-select
                                                )}
                                                disabled={aiSuggestionsLoading[editingExpense.id]}
                                                className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-800 disabled:opacity-50"
                                            >
                                                <Settings className="w-3 h-3" />
                                                {aiSuggestionsLoading[editingExpense.id] ? 'Auto-selecting...' : 'AI Auto-Select'}
                                            </button>
                                        </div>

                                        {/* AI Suggestions for single expense account */}
                                        {aiSuggestions[editingExpense.id] && aiSuggestions[editingExpense.id].length > 0 && (
                                            <div className="mb-2 p-2 bg-purple-50 border border-purple-200 rounded">
                                                <div className="text-xs font-medium text-purple-800 mb-1 flex items-center gap-1">
                                                    <Settings className="w-3 h-3" />
                                                    AI Suggestions ({aiSuggestions[editingExpense.id][0].confidence} confidence)
                                                </div>
                                                <div className="space-y-1">
                                                    {aiSuggestions[editingExpense.id].slice(0, 3).map((suggestion, idx) => (
                                                        <button
                                                            key={idx}
                                                            onClick={() => {
                                                                handleFormDataChange('zoho_expense_account_id', suggestion.account_id);
                                                                handleFormDataChange('zoho_expense_account_name', suggestion.account_name);
                                                                // Clear suggestions after selection
                                                                setAiSuggestions(prev => ({
                                                                    ...prev,
                                                                    [editingExpense.id]: []
                                                                }));
                                                            }}
                                                            className="block w-full text-left px-2 py-1 text-xs bg-white border border-purple-200 rounded hover:bg-purple-50"
                                                        >
                                                            <div className="font-medium">{suggestion.account_name}</div>
                                                            <div className="text-purple-600">{suggestion.reason}</div>
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        <select
                                            value={editFormData.zoho_expense_account_id || ''}
                                            onChange={(e) => {
                                                const selectedAccount = expenseAccounts.find(acc => acc.account_id === e.target.value);
                                                handleFormDataChange('zoho_expense_account_id', e.target.value);
                                                handleFormDataChange('zoho_expense_account_name', selectedAccount?.account_name || '');
                                            }}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        >
                                            <option value="">Select Expense Account</option>
                                            {expenseAccounts.map(account => (
                                                <option key={account.account_id} value={account.account_id}>
                                                    {account.account_name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="p-6 border-t bg-gray-50 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => validateBankAccount(editingExpense)}
                                    className="flex items-center gap-2 px-3 py-2 text-blue-600 hover:text-blue-800"
                                    disabled={!editingExpense.bank_name}
                                >
                                    <Zap className="w-4 h-4" />
                                    Validate Bank Account
                                </button>
                                {editingExpense.has_multiple_items && (
                                    <button
                                        onClick={() => {
                                            setApplyToAllExpense(editingExpense);
                                            setShowApplyToAllModal(true);
                                        }}
                                        className="flex items-center gap-2 px-3 py-2 text-green-600 hover:text-green-800"
                                    >
                                        <CheckCircle2 className="w-4 h-4" />
                                        Apply Account to All Items
                                    </button>
                                )}
                            </div>

                            <div className="flex items-center gap-3">
                                <button
                                    onClick={() => {
                                        setShowEditModal(false);
                                        setEditingExpense(null);
                                        setEditFormData({});
                                    }}
                                    className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={async () => {
                                        await updateExpenseValidation(editingExpense.id, {
                                            payment_account_id: editFormData.zoho_payment_account_id,
                                            payment_account_name: editFormData.zoho_payment_account_name,
                                            expense_account_id: editFormData.zoho_expense_account_id,
                                            expense_account_name: editFormData.zoho_expense_account_name,
                                            validation_status: 'updated'
                                        });
                                        setShowEditModal(false);
                                        setEditingExpense(null);
                                        setEditFormData({});
                                    }}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                                >
                                    Save Changes
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CompleteExpenseManager;